import { OverlayContainer } from '@angular/cdk/overlay'
import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { NgbModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { NgxPaginationModule } from 'ngx-pagination'
import { Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AngularMaterialsModules } from './module/materials.module'
import { AppState } from './ngrx/states'
import { getTheme } from './ngrx/store/selectors'
import { DdcLoggerService } from './service/log/ddc-logger.service'
import { UserService } from './service/user-info/user.service'
import { DdcBottomNavbarComponent } from './shared/ddc/ddc-bottom-navbar/ddc-bottom-navbar.component'
import { DdcFooterComponent } from './shared/ddc/ddc-footer/ddc-footer.component'
import { DdcNavbarComponent } from './shared/ddc/ddc-navbar/ddc-navbar.component'
import { DdcPublicNavbarComponent } from './shared/ddc/ddc-public-navbar/ddc-public-navbar.component'
import { DdcSplashScreenComponent } from './shared/ddc/ddc-splash-screen/ddc-splash-screen.component'
import { Media } from './shared/enums/enums'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    DdcNavbarComponent,
    DdcFooterComponent,
    DdcPublicNavbarComponent,
    DdcBottomNavbarComponent,
    DdcSplashScreenComponent,
    CommonModule,
    ReactiveFormsModule,
    AppRoutingModule,
    FormsModule,
    MatSlideToggleModule,
    AngularMaterialsModules,
    NgbModule,
    NgxPaginationModule,
  ],
  styleUrls: ['./app.component.scss'],
  standalone: true,
})
export class AppComponent implements OnInit {
  title = 'daydic.v1.0'
  _theme$: Observable<string>
  _showNavbar: boolean = false
  _maintenanceMode: boolean = false
  _subscription: string = 'Free Tier' // Default value
  _hasPic: boolean = false
  _isAdmin: boolean = false
  _isProfilePicLoading: boolean = false
  _userProfilePic: string = ''
  _DefaultProfilePic: string = '/assets/images/default-profile.png'
  _username: string = ''
  _isVerified: boolean = false
  _authResolved: boolean = false
  _showSplash: boolean = false
  _isCanvasRoute: boolean = false

  constructor(
    private logger: DdcLoggerService,
    private store: Store<AppState>,
    private router: Router,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private overlayContainer: OverlayContainer,
  ) {
    this._theme$ = this.store.select(getTheme)
  }

  ngOnInit(): void {
    this._theme$ = this.store.select(getTheme)

    // Sync theme class to the CDK overlay container (for tooltips, dialogs, etc.)
    this._theme$.subscribe(theme => {
      const classList = this.overlayContainer.getContainerElement().classList
      classList.remove('dark-theme', 'light-theme', 'paper-theme')
      classList.add(theme)
    })

    // Set up router event listener once (not inside userInfo$ subscription)
    this.setNavbarShowStatus()

    // Subscribe to user info updates
    this.userService.userInfo$.subscribe((userInfo: any) => {
      if (userInfo && userInfo.username) {
        this._hasPic = !!userInfo.profile_pic
        this._isAdmin = userInfo.is_admin || false
        if (environment.maintenance && !this._isAdmin) {
          this.router.navigate(['/maintenance'])
          this._maintenanceMode = true
          this._showNavbar = false
        } else {
          this._maintenanceMode = false
          // Check current URL so a refresh on a public route doesn't show the private navbar
          this._showNavbar = !this.isPublicRoute(this.router.url)
        }
        this.cdr.detectChanges()
        this._isProfilePicLoading = false
        this._userProfilePic = userInfo.profile_pic
          ? Media.ASSETS + userInfo.profile_pic
          : this._DefaultProfilePic
        this._username = userInfo.username || ''
        this._isVerified = userInfo.is_verified || false
        this._subscription = userInfo.subscription || 'Free Tier'
      }
    })

    // Check auth status on app startup
    if (!this.isPublicRoute(this.router.url)) {
      this.userService.checkAuth().subscribe((userInfo: any) => {
        if (userInfo && userInfo.username) {
          this._showSplash = true
        }
        this._authResolved = true
        this.cdr.detectChanges()
      })
    } else {
      this._authResolved = true
    }
  }

  onSplashComplete(): void {
    this._showSplash = false
    this.cdr.detectChanges()
  }

  private isPublicRoute(url: string): boolean {
    return (
      url === '/signup' ||
      url === '/login' ||
      url === '/maintenance' ||
      url === '/verification' ||
      url.startsWith('/reset_password/') ||
      url === '/find_account'
    )
  }

  setNavbarShowStatus(): void {
    this.router.events
      .pipe(
        filter((event: any) => event instanceof NavigationStart || event instanceof NavigationEnd),
      )
      .subscribe((event: any) => {
        if (event instanceof NavigationStart) {
          const isPublic = this.isPublicRoute(event.url)
          this._isCanvasRoute = event.url.startsWith('/canvas')
          if (this._authResolved) {
            this._showNavbar = !isPublic
          }
          this.cdr.detectChanges()
          // Fetch user info when navigating to an authenticated route for the first time
          if (!isPublic && !this._username) {
            this.userService.get_user_info()
          }
        }
      })
  }
}
